import { Suspense } from 'react';
import { Routes, Route, matchPath } from 'react-router-dom';
import { lazyPage, type PreloadablePage } from './shared/lazyPage';
import SiteLayout from './shared/components/SiteLayout';
import LandingLayout from './landing/LandingLayout';
import RequireAuth from './shared/components/RequireAuth';

const InterestConfirm = lazyPage(() => import('./landing/InterestConfirm'));
const InterestManage = lazyPage(() => import('./landing/InterestManage'));
const InterestPrivacy = lazyPage(() => import('./landing/InterestPrivacy'));
const HomePage = lazyPage(() => import('./app/HomePage'));
const BrowsePage = lazyPage(() => import('./app/BrowsePage'));
const CategoryPage = lazyPage(() => import('./app/CategoryPage'));
const SearchPage = lazyPage(() => import('./app/SearchPage'));
const ListingDetailPage = lazyPage(() => import('./app/ListingDetailPage'));
const SellerSpotlightPage = lazyPage(() => import('./app/SellerSpotlightPage'));
const CartPage = lazyPage(() => import('./app/CartPage'));
const CheckoutPage = lazyPage(() => import('./app/CheckoutPage'));
const OrderDetailPage = lazyPage(() => import('./app/OrderDetailPage'));
const SellPage = lazyPage(() => import('./app/SellPage'));
const AboutPage = lazyPage(() => import('./app/AboutPage'));
const PolicyChangelogPage = lazyPage(() => import('./app/PolicyChangelogPage'));
const CategoryHubPage = lazyPage(() => import('./app/CategoryHubPage'));
const BestHandmadeGuidePage = lazyPage(() => import('./app/BestHandmadeGuidePage'));
const BuyerGuidePage = lazyPage(() => import('./app/BuyerGuidePage'));
const CostBreakdownPage = lazyPage(() => import('./app/CostBreakdownPage'));
const EtsyAlternativesPage = lazyPage(() => import('./app/EtsyAlternativesPage'));
const FeeAuditPage = lazyPage(() => import('./app/FeeAuditPage'));
const GiftGuidePage = lazyPage(() => import('./app/GiftGuidePage'));
const LocalPage = lazyPage(() => import('./app/LocalPage'));
const MaterialSourcingGuidePage = lazyPage(() => import('./app/MaterialSourcingGuidePage'));
const SellerGuidePage = lazyPage(() => import('./app/SellerGuidePage'));
const SustainabilityGuidePage = lazyPage(() => import('./app/SustainabilityGuidePage'));
const CorporateGiftingGuidePage = lazyPage(() => import('./app/CorporateGiftingGuidePage'));
const MilestoneGuidePage = lazyPage(() => import('./app/MilestoneGuidePage'));
const CraftTraditionGuidePage = lazyPage(() => import('./app/CraftTraditionGuidePage'));
const ProductCareGuidePage = lazyPage(() => import('./app/ProductCareGuidePage'));
const CertificationGuidePage = lazyPage(() => import('./app/CertificationGuidePage'));
const HandmadeGlossaryPage = lazyPage(() => import('./app/HandmadeGlossaryPage'));
const HolidayCalendarPage = lazyPage(() => import('./app/HolidayCalendarPage'));
const PetGuidePage = lazyPage(() => import('./app/PetGuidePage'));
const NurseryGuidePage = lazyPage(() => import('./app/NurseryGuidePage'));
const NotFoundPage = lazyPage(() => import('./app/NotFoundPage'));

const LoginPage = lazyPage(() => import('./app/LoginPage'));
const RegisterPage = lazyPage(() => import('./app/RegisterPage'));
const VerifyEmailPage = lazyPage(() => import('./app/VerifyEmailPage'));
const ForgotPasswordPage = lazyPage(() => import('./app/ForgotPasswordPage'));
const ResetPasswordPage = lazyPage(() => import('./app/ResetPasswordPage'));
const AccountPage = lazyPage(() => import('./app/AccountPage'));

const SellerOnboardingPage = lazyPage(() => import('./seller/SellerOnboardingPage'));
const SellerOnboardingReturnPage = lazyPage(() => import('./seller/SellerOnboardingReturnPage'));
const SellerOnboardingRefreshPage = lazyPage(() => import('./seller/SellerOnboardingRefreshPage'));
const SellerDashboardPage = lazyPage(() => import('./seller/SellerDashboardPage'));
const SellerListingFormPage = lazyPage(() => import('./seller/SellerListingFormPage'));
const SellerOrdersPage = lazyPage(() => import('./seller/SellerOrdersPage'));
const AdminModerationPage = lazyPage(() => import('./admin/AdminModerationPage'));
const AdminVerificationPage = lazyPage(() => import('./admin/AdminVerificationPage'));
const AdminCatalogReadinessPage = lazyPage(() => import('./admin/AdminCatalogReadinessPage'));
const AdminInviteCodesPage = lazyPage(() => import('./admin/AdminInviteCodesPage'));

/**
 * Routes whose HTML is prerendered at build time (scripts/prerender-guides.mjs). Their page
 * modules must be loaded before rendering on the server and before hydrating on the client.
 */
const PRERENDERED_PAGES: Array<{
  pattern: string;
  page: PreloadablePage;
  after?: (params: Record<string, string | undefined>) => Promise<void>;
}> = [
  { pattern: '/about', page: AboutPage },
  { pattern: '/policy/changelog', page: PolicyChangelogPage },
  { pattern: '/guides/best-handmade/:guideSlug', page: BestHandmadeGuidePage },
  { pattern: '/guides/buyer-guide/:guideSlug', page: BuyerGuidePage },
  { pattern: '/guides/cost-breakdown/:breakdownSlug', page: CostBreakdownPage },
  { pattern: '/guides/etsy-alternatives/:altSlug', page: EtsyAlternativesPage },
  { pattern: '/guides/fee-audit/:auditSlug', page: FeeAuditPage },
  { pattern: '/guides/gift-guide/:guideSlug', page: GiftGuidePage },
  {
    pattern: '/guides/local/:pageSlug',
    page: LocalPage,
    after: async (params) => {
      const { preloadLocalPage } = await import('./app/LocalPage');
      await preloadLocalPage(params.pageSlug);
    },
  },
  { pattern: '/guides/material-sourcing/:guideSlug', page: MaterialSourcingGuidePage },
  { pattern: '/guides/seller-guide/:guideSlug', page: SellerGuidePage },
  { pattern: '/guides/sustainability/:guideSlug', page: SustainabilityGuidePage },
  { pattern: '/guides/corporate-gifting/:guideSlug', page: CorporateGiftingGuidePage },
  { pattern: '/guides/milestone/:guideSlug', page: MilestoneGuidePage },
  { pattern: '/guides/craft-traditions/:guideSlug', page: CraftTraditionGuidePage },
  { pattern: '/guides/product-care/:guideSlug', page: ProductCareGuidePage },
  { pattern: '/guides/certification/:guideSlug', page: CertificationGuidePage },
  { pattern: '/guides/handmade-glossary/:guideSlug', page: HandmadeGlossaryPage },
  { pattern: '/guides/holiday-calendar/:guideSlug', page: HolidayCalendarPage },
  { pattern: '/guides/pet/:guideSlug', page: PetGuidePage },
  { pattern: '/guides/nursery/:guideSlug', page: NurseryGuidePage },
  { pattern: '/guides/:hubSlug', page: CategoryHubPage },
];

/** Loads every lazy module (and content data) the route at `pathname` needs; false if none match. */
export async function preloadRoute(pathname: string): Promise<boolean> {
  for (const { pattern, page, after } of PRERENDERED_PAGES) {
    const match = matchPath({ path: pattern, end: true }, pathname);
    if (!match) continue;
    await page.preload();
    await after?.(match.params);
    return true;
  }
  return false;
}

/** Unknown guide patterns may use the wildcard; prepare its lazy module for the error shell. */
export async function preloadNotFound(): Promise<void> {
  await NotFoundPage.preload();
}

function RouteLoading(): JSX.Element {
  return (
    <div className="mx-auto max-w-md px-4 py-16 text-stone-500 text-sm" role="status">
      Loading...
    </div>
  );
}

export default function App(): JSX.Element {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route element={<LandingLayout />}>
          <Route index element={<HomePage />} />
        </Route>
        <Route element={<LandingLayout joinHref="/#join" />}>
          <Route path="interest/privacy" element={<InterestPrivacy />} />
          <Route path="interest/confirm" element={<InterestConfirm />} />
          <Route path="interest/manage" element={<InterestManage />} />
        </Route>
        <Route element={<LandingLayout maker />}>
          <Route path="sell" element={<SellPage />} />
        </Route>
        <Route element={<SiteLayout />}>
          {/* Public */}
          <Route path="browse" element={<BrowsePage />} />
          <Route path="browse/:categorySlug" element={<CategoryPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="listing/:slug" element={<ListingDetailPage />} />
          <Route path="seller/:shopSlug" element={<SellerSpotlightPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="orders/:orderId" element={<OrderDetailPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="policy/changelog" element={<PolicyChangelogPage />} />
          <Route path="guides/best-handmade/:guideSlug" element={<BestHandmadeGuidePage />} />
          <Route path="guides/buyer-guide/:guideSlug" element={<BuyerGuidePage />} />
          <Route path="guides/cost-breakdown/:breakdownSlug" element={<CostBreakdownPage />} />
          <Route path="guides/etsy-alternatives/:altSlug" element={<EtsyAlternativesPage />} />
          <Route path="guides/fee-audit/:auditSlug" element={<FeeAuditPage />} />
          <Route path="guides/gift-guide/:guideSlug" element={<GiftGuidePage />} />
          <Route path="guides/local/:pageSlug" element={<LocalPage />} />
          <Route path="guides/material-sourcing/:guideSlug" element={<MaterialSourcingGuidePage />} />
          <Route path="guides/seller-guide/:guideSlug" element={<SellerGuidePage />} />
          <Route path="guides/sustainability/:guideSlug" element={<SustainabilityGuidePage />} />
          <Route path="guides/corporate-gifting/:guideSlug" element={<CorporateGiftingGuidePage />} />
          <Route path="guides/milestone/:guideSlug" element={<MilestoneGuidePage />} />
          <Route path="guides/craft-traditions/:guideSlug" element={<CraftTraditionGuidePage />} />
          <Route path="guides/product-care/:guideSlug" element={<ProductCareGuidePage />} />
          <Route path="guides/certification/:guideSlug" element={<CertificationGuidePage />} />
          <Route path="guides/handmade-glossary/:guideSlug" element={<HandmadeGlossaryPage />} />
          <Route path="guides/holiday-calendar/:guideSlug" element={<HolidayCalendarPage />} />
          <Route path="guides/pet/:guideSlug" element={<PetGuidePage />} />
          <Route path="guides/nursery/:guideSlug" element={<NurseryGuidePage />} />
          <Route path="guides/:hubSlug" element={<CategoryHubPage />} />

          {/* Auth */}
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="verify-email" element={<VerifyEmailPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />

          {/* Authed (buyer or seller) */}
          <Route
            path="account"
            element={<RequireAuth><AccountPage /></RequireAuth>}
          />

          {/* Seller (auth required; onboarding page doesn't require profile) */}
          <Route
            path="seller/onboarding"
            element={<RequireAuth><SellerOnboardingPage /></RequireAuth>}
          />
          <Route
            path="seller/onboarding/return"
            element={<RequireAuth requireSellerProfile><SellerOnboardingReturnPage /></RequireAuth>}
          />
          <Route
            path="seller/onboarding/refresh"
            element={<RequireAuth requireSellerProfile><SellerOnboardingRefreshPage /></RequireAuth>}
          />
          <Route
            path="seller/dashboard"
            element={<RequireAuth requireSellerProfile><SellerDashboardPage /></RequireAuth>}
          />
          <Route
            path="seller/listings/new"
            element={<RequireAuth requireSellerProfile><SellerListingFormPage /></RequireAuth>}
          />
          <Route
            path="seller/listings/:id/edit"
            element={<RequireAuth requireSellerProfile><SellerListingFormPage /></RequireAuth>}
          />
          <Route
            path="seller/orders"
            element={<RequireAuth requireSellerProfile><SellerOrdersPage /></RequireAuth>}
          />
          <Route path="admin/moderation" element={<AdminModerationPage />} />
          <Route path="admin/verification" element={<AdminVerificationPage />} />
          <Route path="admin/catalog" element={<AdminCatalogReadinessPage />} />
          <Route path="admin/invites" element={<AdminInviteCodesPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
